import { Injectable } from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { Apollo } from 'apollo-angular';
import { sanitizeUrl } from 'app/shared/helper/validation.helper';
import { GetReleaseNotesGQL } from 'app/shared/notifications-api/graphql';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { ReleaseNote } from './release-notes.interface';

@Injectable({
  providedIn: 'root',
})
export class ReleaseNotesService {
  private notesSubject = new BehaviorSubject<ReleaseNote[]>([
    {
      _id: '',
      version: '',
      releaseDateTS: 0,
      knowledgeCenterUrl: '',
      highlights: [],
    },
  ]);
  public notes$ = this.notesSubject.asObservable();
  public notes: ReleaseNote[] = [];
  private MAX_RETRIEVED_NOTES = 5;
  public module = '';
  
  private readNotesSubject = new BehaviorSubject<string[]>(
    JSON.parse(localStorage.getItem('wind-readReleaseNotes') || '[]')
  );

  constructor(private apollo: Apollo, 
    private getReleaseNotesGQL: GetReleaseNotesGQL) {
  
    this.refreshNotes();
  }

  private drawer: MatDrawer | undefined;


  setDrawer(drawer: MatDrawer) {
    this.drawer = drawer;
  }

  open() {
    if (this.notes.length === 0) {
      return;
    }
    this.drawer?.open();
    const currentRead = this.readNotesSubject.value;
    const allNotes = this.notes.map(note => note._id);
    const merged = Array.from(new Set([...currentRead, ...allNotes]));
    this.saveReadNotes(merged);
  }

  close() {
    this.drawer?.close();
  }

  private refreshNotes(): void {
    this.getReleaseNotes()
      .pipe(take(1))
      .subscribe((currentNotes) => {
        this.notesSubject.next(currentNotes);
      });
  }

  public getReleaseNotes(): Observable<ReleaseNote[]> {
    return this.apollo
      .use('notifications_http')
      .query({
        query: this.getReleaseNotesGQL.document,
        variables: { frontEndName: 'app_windlog', limit: this.MAX_RETRIEVED_NOTES },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map((response: any) => {
          const notes = response.data.releaseNotes.map((note) => ({
            ...note,
            releaseDateTS: note.releaseDateTS * 1000,
            knowledgeCenterUrl: sanitizeUrl(note.knowledgeCenterUrl),
            highlights: note.highlights.map((highlight) => ({
              ...highlight,
              highlightUrl: highlight.highlightUrl
                ? sanitizeUrl(highlight.highlightUrl)
                : highlight.highlightUrl
            }))
          }));
          this.notes = notes;
          return notes;
        })
      );
  }


  public getUnreadNotes$(): Observable<number> {
    return combineLatest([this.notes$, this.readNotesSubject]).pipe(
      map(([notes, readNotes]) => {
        if (notes.length === 1 && notes[0]._id === '') {
          return 0;
        }
        return notes.filter(note => !readNotes.includes(note._id)).length;
      })
    );
  }

  private saveReadNotes(readIds: string[]) {
    localStorage.setItem('wind-readReleaseNotes', JSON.stringify(readIds));
    this.readNotesSubject.next(readIds);
  }

}
